/**
 * OfflineBar — thin red bar that appears directly under the sticky site
 * header whenever the browser loses network connectivity. Because it is
 * rendered as a child of the sticky <header>, it stays pinned together
 * with the navigation on both mobile and desktop.
 */
import { useEffect, useState } from "react";
import { WifiOff } from "lucide-react";
import { useTheme } from "@/lib/theme-context";

export function OfflineBar() {
  const { dir, language } = useTheme();
  const isAr = dir === "rtl";
  const [offline, setOffline] = useState(false);

  useEffect(() => {
    const update = () => setOffline(!navigator.onLine);
    update();
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    return () => {
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
    };
  }, []);

  if (!offline) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="offline-bar"
      className="w-full bg-destructive text-destructive-foreground border-t border-destructive/40 shadow-md animate-fade-in"
    >
      <div className="container mx-auto flex items-center justify-center gap-2 px-3 py-1.5 text-[12px] sm:text-xs font-semibold tracking-tight">
        <WifiOff className="h-3.5 w-3.5 shrink-0" aria-hidden />
        <span>
          {(language === "ar" ? "\u0644\u0627 \u064a\u0648\u062c\u062f \u0627\u062a\u0635\u0627\u0644 \u0628\u0627\u0644\u0625\u0646\u062a\u0631\u0646\u062a \u2014 \u0623\u0646\u062a \u062a\u062a\u0635\u0641\u062d \u062d\u0627\u0644\u064a\u0627\u064b \u0648\u0636\u0639 \u0639\u062f\u0645 \u0627\u0644\u0627\u062a\u0635\u0627\u0644." : "No internet connection \u2014 you are currently browsing in offline mode.")}
        </span>
      </div>
    </div>
  );
}
